import React, {useEffect, useRef} from "react";
import {ModalsRefProps} from "../components/Modals";
import {IImage} from "../stores/models";
import {ModalAction} from "../components/ModalDialog";
import {toI18N} from "./I18n";
import {AlertRefProps} from "../components/Alert";
import {isDefaultGroupImage} from "../stores/GroupStore";

export const showErrorToast = (alertRef: React.RefObject<AlertRefProps>, value: string) => alertRef && alertRef.current && alertRef.current.error(value);
export const showInfoToast = (alertRef: React.RefObject<AlertRefProps>, value: string) => alertRef && alertRef.current && alertRef.current.info(value);

export function createOpenModalFunction(
    modalsRef: React.RefObject<ModalsRefProps>,
    setLocalImage: (value: File) => void,
    setURLImage: (value: string) => void,
    removeImage: () => void,
    image: IImage | null): () => void {

    return () => {

        const newOptions = new Array<ModalAction>(
            {
                value: toI18N('image_picker_select_local_file'), isFileInput: true, onChange: setLocalImage
            },
            {
                value: toI18N('image_picker_select_image_url'), keepModalShown: true, onClick: () => {
                    modalsRef && modalsRef.current && modalsRef.current.showTextInputModal(setURLImage);
                }
            }
        );

        image && !isDefaultGroupImage(image) && newOptions.push({
            value: toI18N('image_picker_remove_image'),
            onClick: removeImage
        });

        newOptions.push({value: toI18N('general_cancel')});

        modalsRef && modalsRef.current && modalsRef.current.showActionsModal(newOptions);
    };

}

export function createInvalidImageFunction(
    alertRef: React.RefObject<AlertRefProps>,
    removeImage: () => void): () => void {

    return () => {
        removeImage();
        showErrorToast(alertRef, toI18N('error_invalid_image'));
    }

}

export function usePrevious(value: any | undefined) {
    const ref = useRef<any>();
    useEffect(() => {
        ref.current = value;
    }, [value]);
    return ref.current;
}

/**
 * A custom useEffect hook that only triggers on updates, not on initial mount
 * Idea stolen from: https://stackoverflow.com/a/55075818/1526448
 * @param {Function} effect
 * @param {Array<any>} dependencies
 */
export default function useUpdateEffect(effect: Function, dependencies: Array<any> = []) {
    const isInitialMount = useRef(true);

    useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false;
        } else {
            effect();
        }
        // eslint-disable-next-line
    }, [effect, ...dependencies]);
}